import BaseService from 'services/BaseService';
import {
  ItemStatus,
  Learning,
  Region,
  Rule,
  RunReport,
  RunSummary,
  SeoItem,
  SeoSettings,
  ThreadEntry,
  TypeStats,
} from 'pages/SeoGrowth/types';

// Reads return null on failure (BaseService convention, shown as an empty
// state). Writes (decisions, questions, rules) THROW with the server's message
// instead — publishing to the live blog must never fail silently.
class SeoGrowthService extends BaseService {
  basePath = 'v1/seo-growth';

  // Which store's queue, runs and settings every call is about. Standing rules
  // are shared, so the rules calls ignore it.
  region: Region = 'US';

  private get regionParams() {
    return { region: this.region };
  }

  async listItems(status?: ItemStatus) {
    await this._getAccessToken();
    return this.handleHttpRequest<{ items: SeoItem[] }>(
      () =>
        this.http.get(`${this.basePath}/items`, {
          params: { status, ...this.regionParams },
        }),
      'Failed to load the SEO review queue',
    );
  }

  async getSettings() {
    await this._getAccessToken();
    return this.handleHttpRequest<{ settings: SeoSettings }>(
      () =>
        this.http.get(`${this.basePath}/settings`, {
          params: this.regionParams,
        }),
      'Failed to load SEO settings',
    );
  }

  async getStats() {
    await this._getAccessToken();
    return this.handleHttpRequest<{ stats: Record<string, TypeStats> }>(
      () =>
        this.http.get(`${this.basePath}/stats`, { params: this.regionParams }),
      'Failed to load SEO stats',
    );
  }

  async listRuns() {
    await this._getAccessToken();
    return this.handleHttpRequest<{ runs: RunSummary[] }>(
      () =>
        this.http.get(`${this.basePath}/runs`, { params: this.regionParams }),
      'Failed to load the daily runs',
    );
  }

  async getRun(id: string) {
    await this._getAccessToken();
    return this.handleHttpRequest<{ run: RunReport }>(
      () =>
        this.http.get(`${this.basePath}/runs/${encodeURIComponent(id)}`, {
          params: this.regionParams,
        }),
      'Failed to load the run report',
    );
  }

  async listRules() {
    await this._getAccessToken();
    return this.handleHttpRequest<{ rules: Rule[] }>(
      () => this.http.get(`${this.basePath}/rules`),
      'Failed to load the rules',
    );
  }

  async getLearning() {
    await this._getAccessToken();
    return this.handleHttpRequest<Learning>(
      () =>
        this.http.get(`${this.basePath}/learning`, {
          params: this.regionParams,
        }),
      'Failed to load what the system has learned',
    );
  }

  async askAboutItem(id: string, question: string, askedBy: string) {
    const data = await this.write(
      'post',
      `items/${encodeURIComponent(id)}/ask`,
      {
        question,
        asked_by: askedBy,
      },
    );
    return data as { entry: ThreadEntry; item: SeoItem };
  }

  async askAboutRun(id: string, question: string, askedBy: string) {
    const data = await this.write(
      'post',
      `runs/${encodeURIComponent(id)}/ask`,
      {
        question,
        asked_by: askedBy,
      },
    );
    return data as { entry: ThreadEntry; run: RunReport };
  }

  async addRule(text: string, createdBy: string) {
    const data = await this.write('post', 'rules', {
      text,
      created_by: createdBy,
    });
    return data.rule as Rule;
  }

  async updateRule(id: string, patch: { text?: string; active?: boolean }) {
    const data = await this.write(
      'put',
      `rules/${encodeURIComponent(id)}`,
      patch,
    );
    return data.rule as Rule;
  }

  async approve(id: string, decidedBy: string) {
    return this.decide(`items/${encodeURIComponent(id)}/approve`, {
      decided_by: decidedBy,
    });
  }

  async undo(id: string, decidedBy: string) {
    return this.decide(`items/${encodeURIComponent(id)}/undo`, {
      decided_by: decidedBy,
    });
  }

  async reject(id: string, decidedBy: string, reason: string) {
    return this.decide(`items/${encodeURIComponent(id)}/reject`, {
      decided_by: decidedBy,
      reason,
    });
  }

  async requestChanges(id: string, decidedBy: string, feedback: string) {
    return this.decide(`items/${encodeURIComponent(id)}/request-changes`, {
      decided_by: decidedBy,
      feedback,
    });
  }

  async saveSettings(patch: Partial<SeoSettings>) {
    await this._getAccessToken();
    try {
      const resp = await this.http.put(`${this.basePath}/settings`, patch, {
        params: this.regionParams,
      });
      return resp.data.settings as SeoSettings;
    } catch (err: any) {
      throw new Error(
        err?.response?.data?.error || err?.message || 'Failed to save settings',
      );
    }
  }

  private async decide(
    path: string,
    body: Record<string, string>,
  ): Promise<SeoItem> {
    const data = await this.write('post', path, body);
    return data.item as SeoItem;
  }

  private async write(
    method: 'post' | 'put',
    path: string,
    body: Record<string, unknown>,
  ): Promise<any> {
    await this._getAccessToken();
    try {
      const resp = await this.http[method](`${this.basePath}/${path}`, body, {
        params: this.regionParams,
      });
      return resp.data;
    } catch (err: any) {
      throw new Error(
        err?.response?.data?.error || err?.message || 'Request failed',
      );
    }
  }
}

export default SeoGrowthService;
